import { useState, useRef, forwardRef } from 'react';
import DatePicker from 'react-datepicker';
import { registerLocale, setDefaultLocale } from 'react-datepicker';
import { ar } from 'date-fns/locale';
import 'react-datepicker/dist/react-datepicker.css';
import './UnifiedDatePicker.scss';

// Register Arabic locale for react-datepicker
registerLocale('ar', ar);
setDefaultLocale('ar');

const UnifiedDatePicker = ({
  value,
  onChange,
  placeholder = "اختر التاريخ",
  className = "",
  style = {},
  disabled = false,
  minDate = null,
  maxDate = null,
  showTimeSelect = false,
  timeFormat = "HH:mm",
  dateFormat = "yyyy-MM-dd",
  timeIntervals = 15,
  showTimeSelectOnly = false,
  showYearDropdown = false,
  showMonthDropdown = false,
  dropdownMode = "select",
  yearDropdownItemNumber = 15,
  scrollableYearDropdown = true,
  inline = false,
  withPortal = false,
  popperClassName = "",
  popperPlacement = "bottom",
  portalId = "root",

  autoComplete = "off",
  name = "",
  id = "",
  required = false,
  readOnly = false,
  tabIndex = 0,
  onFocus = null,
  onBlur = null,
  onKeyDown = null,
  onCalendarOpen = null,
  onCalendarClose = null,
  customInput = null,
  wrapperClassName = "",
  ...props
}) => {
  const [, setIsOpen] = useState(false);
  const [focused, setFocused] = useState(false);
  const datePickerRef = useRef(null);

  // Handle focus events
  const handleFocus = (e) => {
    setFocused(true);
    if (onFocus) onFocus(e);
  };

  const handleBlur = (e) => {
    setFocused(false);
    if (onBlur) onBlur(e);
  };

  // Handle calendar open/close
  const handleCalendarOpen = () => {
    setIsOpen(true);
    if (onCalendarOpen) onCalendarOpen();
  };

  const handleCalendarClose = () => {
    setIsOpen(false);
    if (onCalendarClose) onCalendarClose();
  };

  // Convert ISO string to Date object
  const getDateValue = () => {
    if (!value) return null;
    if (value instanceof Date) return value;
    if (typeof value === 'string') {
      const date = new Date(value);
      return isNaN(date.getTime()) ? null : date;
    }
    return null;
  };

  // Handle date change
  const handleDateChange = (date) => {
    if (onChange) {
      if (date) {
        // Always return ISO format string
        onChange(date.toISOString().split('T')[0]);
      } else {
        onChange('');
      }
    }
  };

  // Custom input component for consistent styling
  const CustomInput = forwardRef(({ value, onClick, onChange, onBlur, onFocus, placeholder, disabled, readOnly, ...inputProps }, ref) => (
    <input
      ref={ref}
      type="text"
      value={value}
      onClick={onClick}
      onChange={onChange}
      onBlur={onBlur}
      onFocus={onFocus}
      placeholder={placeholder}
      disabled={disabled}
      readOnly={readOnly}
      autoComplete={autoComplete}
      name={name}
      id={id}
      required={required}
      tabIndex={tabIndex}
      className={`unified-date-picker-input ${className} ${focused ? 'focused' : ''} ${disabled ? 'disabled' : ''}`}
      style={{
        ...style,
        direction: 'rtl',
        textAlign: 'right',
        fontFamily: 'Cairo, sans-serif',
      }}
      {...inputProps}
    />
  ));

  CustomInput.displayName = 'CustomInput';



  return (
    <div className={`unified-date-picker-wrapper ${wrapperClassName}`}>
      <DatePicker
        ref={datePickerRef}
        selected={getDateValue()}
        onChange={handleDateChange}
        onFocus={handleFocus}
        onBlur={handleBlur}
        onKeyDown={onKeyDown}
        onCalendarOpen={handleCalendarOpen}
        onCalendarClose={handleCalendarClose}
        placeholderText={placeholder}
        disabled={disabled}
        readOnly={readOnly}
        minDate={minDate}
        maxDate={maxDate}
        showTimeSelect={showTimeSelect}
        timeFormat={timeFormat}
        dateFormat={dateFormat}
        timeIntervals={timeIntervals}
        showTimeSelectOnly={showTimeSelectOnly}
        showYearDropdown={showYearDropdown}
        showMonthDropdown={showMonthDropdown}
        dropdownMode={dropdownMode}
        yearDropdownItemNumber={yearDropdownItemNumber}
        scrollableYearDropdown={scrollableYearDropdown}
        inline={inline}
        withPortal={withPortal}
        popperClassName={`unified-date-picker-popper ${popperClassName}`}
        portalId={portalId}
        popperPlacement={popperPlacement}
        customInput={customInput || <CustomInput />}
        locale="ar"
        isRTL={true}
        autoComplete={autoComplete}
        name={name}
        id={id}
        required={required}
        tabIndex={tabIndex}
        {...props}
      />
    </div>
  );
};

export default UnifiedDatePicker;
